import { Component, OnInit, ViewChild } from '@angular/core';
import { ListService, PagedResultDto } from '@abp/ng.core';
import { Column } from '../shared/components/metro-table/metro-table.component';
import { GetSommelierDto, SommelierDeletionInfoDto, SommelierDto } from '../proxy/sommeliers/models';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SommelierFormComponent } from './create-edit-sommelier/sommelier-form.component';
import { ConfirmationModalService } from '../shared/services/confirmation-modal.service';
import { SommelierService } from '@proxy/sommeliers';
import { Router } from '@angular/router';
import { APP_ICONS } from '../shared/icons/app-icons';
import { finalize, take } from 'rxjs';
import { NotificationService } from '../shared/services/notification.service';

@Component({
  selector: 'app-sommeliers',
  templateUrl: './sommeliers.component.html',
  providers: [ListService],
  standalone: false,
})
export class SommeliersComponent implements OnInit {
  columns: Column[] = [
    { name: 'fullName', label: '::Name', width: '20%' },
    { name: 'email', label: '::Email', width: '20%', textColor: 'text-metro-blue-secondary' },
    { name: 'slug', label: '::Slug', width: '15%', textColor: 'text-metro-blue-secondary' },
    { name: 'creationTime', label: 'Registro', width: '15%', type: 'date' },
    // winepassport#385 — "Estado" wrappeaba en el header con 10%.
    { name: 'status', label: '::Status', width: '13%', textColor: 'text-metro-blue-secondary', type: 'status' },
  ];

  actions: any[] = [
    {
      name: 'edit',
      label: '::Edit',
      handler: (row: SommelierDto) => this.openEditModal(row),
    },
    {
      // winepassport#349 (WP-003) — "dar de baja" (antes "desactivar"). Además de apagar
      // el flag, ahora el backend deja el estado en "DroppedOut".
      name: 'deactivate',
      label: '::DropOut',
      handler: (row: SommelierDto) => this.deactivateSommelier(row),
      condition: (row) =>
        row.isActive === true &&
        row.status === 'Approved' &&
        !!row.userId
    },
    {
      name: 'reject',
      label: '::Rejected',
      handler: (row: SommelierDto) => this.rejectSommelier(row),
      condition: (row) => row.status === 'Pending',
    },
    {
      name: 'activate',
      label: '::Activate',
      handler: (row: SommelierDto) => this.activateSommelier(row),
      // winepassport#349: un sommelier dado de baja tiene status "DroppedOut"; se
      // contempla también el estado viejo ("Approved" + inactivo) por si quedó alguno.
      condition: (row) =>
        !!row.userId &&
        (row.status === 'DroppedOut' ||
          (row.status === 'Approved' && row.isActive === false))
    },
    {
      // winepassport#349 (WP-003) — eliminar de verdad un duplicado sin historial.
      name: 'hardDelete',
      label: '::Delete',
      handler: (row: SommelierDto) => this.hardDeleteSommelier(row),
      condition: (row) => this.deletionInfo[row.id]?.canHardDelete === true,
    },
  ];


  data: PagedResultDto<SommelierDto> = { items: [], totalCount: 0 };
  searchSommelier = {} as GetSommelierDto;
  isLoading = true;
  isExporting = false;

  showSommelierModal = false;
  selectedSommelier: SommelierDto | null = null;

  // winepassport#339 / CU-44 — baja de sommelier con reasignación de clientes
  showReassignModal = false;
  reassignSommelier: SommelierDto | null = null;
  reassignClientCount = 0;
  reassignTargetId: number | null = null;
  reassignUseInstitutional = false;
  reassignSubmitting = false;
  activeSommelierOptions: { value: number; label: string }[] = [];

  excelIcon = APP_ICONS.excel;
  statusOptions = [
    { value: '', label: '::All' },
    { value: 'Pending', label: '::Pending' },
    { value: 'Approved', label: '::Approved' },
    { value: 'DroppedOut', label: '::DroppedOut' },
    { value: 'Rejected', label: '::Rejected' },
  ];

  // winepassport#349 (WP-003) — cache de "¿se puede eliminar?" por sommelier de la página
  // actual (0 clientes y 0 órdenes → es un duplicado sin historial).
  deletionInfo: Record<number, SommelierDeletionInfoDto> = {};

  // Guard contra clicks duplicados en rejectSommelier
  rejectingIds = new Set<number>();

  @ViewChild(SommelierFormComponent) sommelierForm?: SommelierFormComponent;

  constructor(
    public readonly list: ListService,
    private sommelierService: SommelierService,
    private confirmation: ConfirmationModalService,
    private router: Router,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    const streamCreator = (query) =>
      this.sommelierService.getList({ ...this.searchSommelier, ...query });

    this.list.hookToQuery(streamCreator).subscribe((result) => {
      this.data = result;
      this.isLoading = false;
      this.loadDeletionInfo(result.items);
    });
  }

  // winepassport#349 (WP-003) — para cada sommelier de la página pregunta si se puede
  // borrar (sin clientes ni órdenes). Se cachea por id; si falla una, se ignora (la acción
  // "Eliminar" simplemente no aparece).
  private loadDeletionInfo(items: SommelierDto[]) {
    this.deletionInfo = {};
    const pending = (items ?? []).filter((s) => !!s.id);
    if (pending.length === 0) return;
    forkJoin(
      pending.map((s) =>
        // skipHandleError: si el endpoint falla (404 con backend viejo, error transitorio)
        // no dispara el modal global; simplemente no se muestra la acción "Eliminar".
        this.sommelierService.getDeletionInfo(s.id, { skipHandleError: true } as any).pipe(
          map((info) => ({ id: s.id, info })),
          catchError(() => of(null)),
        ),
      ),
    ).subscribe((results) => {
      const next: Record<number, SommelierDeletionInfoDto> = {};
      for (const r of results) {
        if (r) next[r.id] = r.info;
      }
      this.deletionInfo = next;
    });
  }

  hardDeleteSommelier(sommelier: SommelierDto) {
    this.confirmation.confirm({
      title: '::AreYouSureToDelete',
      message: '::SommelierHardDeleteWarning',
      type: 'danger',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.sommelierService.hardDelete(sommelier.id).subscribe(() => this.list.get());
    });
  }

  onSearch(searchTerm: string) {
    this.isLoading = true;
    this.searchSommelier.keyword = searchTerm;
    this.list.page = 0;
    this.list.get();
  }

  onStatusChange(status: string) {
    this.isLoading = true;
    this.searchSommelier.status = status;
    this.list.page = 0;
    this.list.get();
  }

  onClearSearch() {
    this.isLoading = true;
    this.searchSommelier = {} as GetSommelierDto;
    this.list.page = 0;
    this.list.get();
  }

  handleSommelierSaved(sommelier: SommelierDto) {
    this.list.get();
    this.showSommelierModal = false;
  }

  generateExcel() {
    this.isExporting = true;

    this.sommelierService.generateExcel({ responseType: 'blob' } as any)
      .pipe(
        take(1),
        finalize(() => this.isExporting = false)
      )
      .subscribe({
        next: (blob) => {
          const fileName = 'Sommeliers.xlsx';
          const downloadUrl = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName;
          link.click();

          window.URL.revokeObjectURL(downloadUrl);
        },
        error: (err) => {
          console.error('Error al generar Excel de sommeliers', err);
        }
      });
  }

  openCreateModal() {
    this.router.navigate(['/sommeliers/create']);
  }

  openEditModal(sommelier: SommelierDto) {
    this.router.navigate(['/sommeliers/edit', sommelier.id]);
  }

  deleteSommelier(sommelier: SommelierDto) {
    this.confirmation.confirm({
      title: '::AreYouSureToDelete',
      message: '::ThisActionCannotBeUndone',
      type: 'danger',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.sommelierService.delete(sommelier.id).subscribe(() => this.list.get());
    });
  }

  onPageChange(page: number) {
    this.isLoading = true;
    this.list.page = page - 1;  // Convert from 1-indexed (used by metro-table) to 0-indexed (used by ABP ListService)
    this.list.get();
  }

  activateSommelier(sommelier: SommelierDto) {
    this.confirmation.confirm({
      title: '::ConfirmActivate',
      message: '::SommelierWillBeActivated',
      // type: 'primary',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;

      this.sommelierService
        .enable(sommelier.id)
        .subscribe(() => this.list.get());
    });
  }

  deactivateSommelier(sommelier: SommelierDto) {
    // #339: si el sommelier tiene clientes, hay que reasignarlos antes de desactivar.
    this.sommelierService.getClientCount(sommelier.id).subscribe((count) => {
      if (count > 0) {
        this.openReassignModal(sommelier, count);
        return;
      }
      this.confirmation.confirm({
        title: '::ConfirmDeactivate',
        message: '::SommelierWillBeDeactivated',
        type: 'danger',
        showCancelButton: true,
      }).subscribe((confirmed) => {
        if (!confirmed) return;
        this.sommelierService
          .disableWithReassign({ sommelierId: sommelier.id, useInstitutional: false })
          .subscribe(() => this.list.get());
      });
    });
  }

  rejectSommelier(sommelier: SommelierDto) {
    if (this.rejectingIds.has(sommelier.id)) return;

    this.confirmation.confirm({
      title: '::Rejected',
      message: '¿Querés rechazar esta postulación?',
      type: 'danger',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.rejectingIds.add(sommelier.id);
      this.sommelierService.rejectSommelier(sommelier.id)
        .pipe(finalize(() => this.rejectingIds.delete(sommelier.id)))
        .subscribe({
          next: () => {
            this.notificationService.success('', 'Postulación rechazada correctamente');
            this.list.get();
          },
          error: (err) => {
            console.error('Error al rechazar sommelier:', err);
            this.notificationService.error('', 'Error al rechazar la postulación. Intentá nuevamente.');
          }
        });
    });
  }

  private openReassignModal(sommelier: SommelierDto, clientCount: number) {
    this.reassignSommelier = sommelier;
    this.reassignClientCount = clientCount;
    this.reassignTargetId = null;
    this.reassignUseInstitutional = false;
    this.activeSommelierOptions = [];
    this.showReassignModal = true;

    this.sommelierService
      .getList({ status: 'Approved', maxResultCount: 1000 } as GetSommelierDto)
      .subscribe((res) => {
        this.activeSommelierOptions = res.items
          .filter((s) => s.isActive === true && s.id !== sommelier.id)
          .map((s) => ({ value: s.id, label: `${(s as any).fullName ?? (s.name + ' ' + s.surname)} — ${s.slug}` }));
      });
  }

  closeReassignModal() {
    this.showReassignModal = false;
    this.reassignSommelier = null;
  }

  confirmReassign() {
    if (!this.reassignSommelier || this.reassignSubmitting) return;
    if (!this.reassignUseInstitutional && !this.reassignTargetId) return;

    this.reassignSubmitting = true;
    this.sommelierService
      .disableWithReassign({
        sommelierId: this.reassignSommelier.id,
        targetSommelierId: this.reassignUseInstitutional ? null : this.reassignTargetId,
        useInstitutional: this.reassignUseInstitutional,
      })
      .pipe(finalize(() => (this.reassignSubmitting = false)))
      .subscribe(() => {
        this.showReassignModal = false;
        this.reassignSommelier = null;
        this.list.get();
      });
  }

}
